import { createContext, useContext, useState, useEffect, useCallback, useRef } from "react";
import { useSelector } from "react-redux";
import Peer from "simple-peer";

const CallContext = createContext();

export const useCall = () => {
  const context = useContext(CallContext);
  if (!context) {
    throw new Error("useCall must be used within CallProvider");
  }
  return context;
};

export const CallProvider = ({ children, socket, disabled = false }) => {
  const user = useSelector((state) => state.user);

  // Call state
  const [callState, setCallState] = useState({
    isInCall: false,
    isRinging: false,
    isIncoming: false,
    callId: null,
    callType: null, // "voice" or "video"
    caller: null,
    targetUser: null,
    conversationId: null,
  });

  // Media state
  const [mediaState, setMediaState] = useState({
    isAudioEnabled: true,
    isVideoEnabled: true,
    isScreenSharing: false,
    localStream: null,
    remoteStream: null,
  });

  // Peer connection
  const peerRef = useRef(null);
  const localVideoRef = useRef(null);
  const remoteVideoRef = useRef(null);

  // Initialize call
  const initiateCall = useCallback(async (targetUserId, conversationId, type = "video") => {
    if (!socket || callState.isInCall) return;

    try {
      // Get user media
      const stream = await navigator.mediaDevices.getUserMedia({
        video: type === "video",
        audio: true,
      });

      setMediaState((prev) => ({
        ...prev,
        localStream: stream,
        isVideoEnabled: type === "video",
      }));

      if (localVideoRef.current) {
        localVideoRef.current.srcObject = stream;
      }

      setCallState({
        isInCall: false,
        isRinging: true,
        isIncoming: false,
        callId: null,
        callType: type,
        caller: user,
        targetUser: { _id: targetUserId },
        conversationId,
      });

      socket.emit("call:initiate", {
        targetUserId,
        conversationId,
        type,
      });
    } catch (error) {
      console.error("Error initiating call:", error);
      alert("Erro ao acessar camera/microfone. Verifique as permissoes.");
    }
  }, [socket, callState.isInCall, user]);

  // Answer incoming call
  const answerCall = useCallback(async () => {
    if (!socket || !callState.isIncoming || !callState.callId) return;

    try {
      // Get user media
      const stream = await navigator.mediaDevices.getUserMedia({
        video: callState.callType === "video",
        audio: true,
      });

      setMediaState((prev) => ({
        ...prev,
        localStream: stream,
        isVideoEnabled: callState.callType === "video",
      }));

      if (localVideoRef.current) {
        localVideoRef.current.srcObject = stream;
      }

      // Create peer connection (answerer - not initiator)
      const peer = new Peer({
        initiator: false,
        trickle: false,
        stream,
      });

      peer.on("signal", (signal) => {
        socket.emit("call:answer", {
          callId: callState.callId,
          signal,
        });
      });

      peer.on("stream", (remoteStream) => {
        setMediaState((prev) => ({
          ...prev,
          remoteStream,
        }));
        if (remoteVideoRef.current) {
          remoteVideoRef.current.srcObject = remoteStream;
        }
      });

      peer.on("error", (err) => {
        console.error("Peer error:", err);
        endCall();
      });

      peerRef.current = peer;

      setCallState((prev) => ({
        ...prev,
        isInCall: true,
        isRinging: false,
        isIncoming: false,
      }));
    } catch (error) {
      console.error("Error answering call:", error);
      rejectCall();
    }
  }, [socket, callState]);

  // Reject incoming call
  const rejectCall = useCallback(() => {
    if (!socket || !callState.callId) return;

    socket.emit("call:reject", {
      callId: callState.callId,
    });

    resetCallState();
  }, [socket, callState.callId]);

  // End call
  const endCall = useCallback(() => {
    if (socket && callState.callId) {
      socket.emit("call:end", {
        callId: callState.callId,
      });
    }

    // Stop all tracks
    if (mediaState.localStream) {
      mediaState.localStream.getTracks().forEach((track) => track.stop());
    }

    // Close peer connection
    if (peerRef.current) {
      peerRef.current.destroy();
      peerRef.current = null;
    }

    resetCallState();
  }, [socket, callState.callId, mediaState.localStream]);

  // Reset call state
  const resetCallState = useCallback(() => {
    setCallState({
      isInCall: false,
      isRinging: false,
      isIncoming: false,
      callId: null,
      callType: null,
      caller: null,
      targetUser: null,
      conversationId: null,
    });

    setMediaState({
      isAudioEnabled: true,
      isVideoEnabled: true,
      isScreenSharing: false,
      localStream: null,
      remoteStream: null,
    });
  }, []);

  // Toggle audio
  const toggleAudio = useCallback(() => {
    if (mediaState.localStream) {
      const audioTrack = mediaState.localStream.getAudioTracks()[0];
      if (audioTrack) {
        audioTrack.enabled = !audioTrack.enabled;
        setMediaState((prev) => ({
          ...prev,
          isAudioEnabled: audioTrack.enabled,
        }));
      }
    }
  }, [mediaState.localStream]);

  // Toggle video
  const toggleVideo = useCallback(() => {
    if (mediaState.localStream) {
      const videoTrack = mediaState.localStream.getVideoTracks()[0];
      if (videoTrack) {
        videoTrack.enabled = !videoTrack.enabled;
        setMediaState((prev) => ({
          ...prev,
          isVideoEnabled: videoTrack.enabled,
        }));
      }
    }
  }, [mediaState.localStream]);

  // Start screen sharing
  const startScreenShare = useCallback(async () => {
    if (!peerRef.current || mediaState.isScreenSharing) return;

    try {
      const screenStream = await navigator.mediaDevices.getDisplayMedia({
        video: true,
        audio: false,
      });

      const screenTrack = screenStream.getVideoTracks()[0];

      // Replace video track in peer
      const sender = peerRef.current._pc
        .getSenders()
        .find((s) => s.track?.kind === "video");

      if (sender) {
        sender.replaceTrack(screenTrack);
      }

      screenTrack.onended = () => {
        stopScreenShare();
      };

      setMediaState((prev) => ({
        ...prev,
        isScreenSharing: true,
      }));

      if (localVideoRef.current) {
        localVideoRef.current.srcObject = screenStream;
      }
    } catch (error) {
      console.error("Error sharing screen:", error);
    }
  }, [mediaState.isScreenSharing]);

  // Stop screen sharing
  const stopScreenShare = useCallback(async () => {
    if (!peerRef.current || !mediaState.isScreenSharing) return;

    try {
      // Get new video stream
      const stream = await navigator.mediaDevices.getUserMedia({
        video: true,
        audio: false,
      });

      const videoTrack = stream.getVideoTracks()[0];

      // Replace track in peer
      const sender = peerRef.current._pc
        .getSenders()
        .find((s) => s.track?.kind === "video");

      if (sender) {
        sender.replaceTrack(videoTrack);
      }

      setMediaState((prev) => ({
        ...prev,
        isScreenSharing: false,
        localStream: stream,
      }));

      if (localVideoRef.current) {
        localVideoRef.current.srcObject = stream;
      }
    } catch (error) {
      console.error("Error stopping screen share:", error);
    }
  }, [mediaState.isScreenSharing]);

  // Socket event handlers
  useEffect(() => {
    // Don't register handlers if disabled (chat page handles calls itself)
    if (!socket || disabled) return;

    // Incoming call
    socket.on("call:incoming", (data) => {
      console.log("Incoming call:", data);
      setCallState({
        isInCall: false,
        isRinging: true,
        isIncoming: true,
        callId: data.callId,
        callType: data.type,
        caller: data.caller,
        targetUser: null,
        conversationId: data.conversationId,
      });
    });

    // Call initiated (confirmation)
    socket.on("call:initiated", (data) => {
      console.log("Call initiated:", data);
      setCallState((prev) => ({
        ...prev,
        callId: data.callId,
      }));
    });

    // Call answered
    socket.on("call:answered", (data) => {
      console.log("Call answered:", data);

      if (peerRef.current) {
        peerRef.current.signal(data.signal);
      } else {
        // Create peer connection (caller - initiator)
        const peer = new Peer({
          initiator: true,
          trickle: false,
          stream: mediaState.localStream,
        });

        peer.on("signal", (signal) => {
          socket.emit("call:signal", {
            callId: data.callId,
            targetUserId: data.answeredBy,
            signal,
          });
        });

        peer.on("stream", (remoteStream) => {
          setMediaState((prev) => ({
            ...prev,
            remoteStream,
          }));
          if (remoteVideoRef.current) {
            remoteVideoRef.current.srcObject = remoteStream;
          }
        });

        peer.on("error", (err) => {
          console.error("Peer error:", err);
          endCall();
        });

        peerRef.current = peer;
      }

      setCallState((prev) => ({
        ...prev,
        isInCall: true,
        isRinging: false,
      }));
    });

    // Call rejected
    socket.on("call:rejected", (data) => {
      console.log("Call rejected:", data);
      if (mediaState.localStream) {
        mediaState.localStream.getTracks().forEach((track) => track.stop());
      }
      resetCallState();
    });

    // Call ended
    socket.on("call:ended", (data) => {
      console.log("Call ended:", data);
      endCall();
    });

    // Call missed
    socket.on("call:missed", (data) => {
      console.log("Call missed:", data);
      if (mediaState.localStream) {
        mediaState.localStream.getTracks().forEach((track) => track.stop());
      }
      resetCallState();
    });

    // Call unavailable (user offline)
    socket.on("call:unavailable", (data) => {
      console.log("Call unavailable:", data);
      if (mediaState.localStream) {
        mediaState.localStream.getTracks().forEach((track) => track.stop());
      }
      resetCallState();
      alert(data.message || "Usuario nao disponivel");
    });

    // WebRTC signal
    socket.on("call:signal", (data) => {
      console.log("Signal received:", data);
      if (peerRef.current) {
        peerRef.current.signal(data.signal);
      }
    });

    return () => {
      socket.off("call:incoming");
      socket.off("call:initiated");
      socket.off("call:answered");
      socket.off("call:rejected");
      socket.off("call:ended");
      socket.off("call:missed");
      socket.off("call:unavailable");
      socket.off("call:signal");
    };
  }, [socket, mediaState.localStream, endCall, resetCallState, disabled]);

  const value = {
    callState,
    mediaState,
    localVideoRef,
    remoteVideoRef,
    initiateCall,
    answerCall,
    rejectCall,
    endCall,
    toggleAudio,
    toggleVideo,
    startScreenShare,
    stopScreenShare,
  };

  return (
    <CallContext.Provider value={value}>
      {children}
    </CallContext.Provider>
  );
};

export default CallContext;
